<script setup>
import { i18n } from 'boot/i18n';
import {
  HrConfirmBtn,
  HrDismissBtn,
  HrFormDialog,
  HrInfoBanner,
} from 'src/design-system';
import { computed, ref } from 'vue';

import CalendarDateField from './CalendarDateField.vue';

/**
 * Close one of the clinic's own codes on a date, or reopen one that is closed.
 *
 * A DIALOG RATHER THAN A CONFIRM, because the action carries a value. `confirmDialog` asks a yes/no
 * question and `promptDialog` collects a string; a retirement collects a CALENDAR DATE, and a date
 * typed as free text is the one thing this context refuses everywhere else — an unreadable bound is
 * how a record ends up with no position in time. `CalendarDateField` is the context's own answer to
 * that and is reused here rather than a second date control being invented.
 *
 * IT SAYS "LAST BILLABLE DATE", NOT "RETIRED FROM", and the wording is load-bearing rather than
 * fussy. `retiredOn` is inclusive: a code retired 2026-12-31 bills through that day. The design's
 * "Retired from <date>" reads as the first NON-billable day, which is off by one — and one day of
 * services either billed or held wrongly is exactly the kind of error nobody notices until a payer
 * does.
 *
 * REVERSIBLE, INCLUDING AFTER THE DATE HAS PASSED. Clearing the field un-retires the code. That
 * looks like rewriting history and is not: closing a window makes a code unbillable for NEW service
 * dates and leaves every past one resolving exactly as before, so reopening returns a capability
 * rather than altering a record. "Our funder changed their mind" is more common than the mistake it
 * resembles.
 */
defineOptions({ name: 'RetireServiceCodeDialog' });

const props = defineProps({
  /** The code string, for the title — this dialog never edits it. */
  code: {
    type: String,
    required: true,
  },
  /** The retirement currently on the code, or null when it is open-ended. */
  retiredOn: {
    type: String,
    default: null,
  },
  /**
   * The date the code starts, as a 'YYYY-MM-DD' calendar string.
   *
   * The lower bound on the calendar. A retirement before it closes a window that never opened: the
   * code becomes billable on no date at all, and nothing about the row would look wrong.
   */
  validFrom: {
    type: String,
    default: null,
  },
});

const emit = defineEmits({
  /** Fired with the chosen date, or null to reopen the code. */
  ok: null,
  /** Fired when the dialog is dismissed without changing anything. */
  hide: null,
});

const dialog = ref(null);
const lastBillableOn = ref(props.retiredOn);

/**
 * Reopening is clearing a retirement that EXISTS, which is why this reads the prop and not the
 * field. Seeded from `retiredOn`, an open-ended code starts with an empty field, and treating empty
 * as the intent to reopen greeted it with the opposite of the action the user had just chosen.
 */
const isReopening = computed(() => props.retiredOn !== null && lastBillableOn.value === null);

/** Nothing to submit until the field differs from what the code already says. */
const hasChange = computed(() => lastBillableOn.value !== props.retiredOn);

function hide() {
  dialog.value?.hide();
}

defineExpose({
  show: () => dialog.value?.show(),
  hide,
});

const onSubmit = () => {
  emit('ok', lastBillableOn.value);
  hide();
};
</script>

<template>
  <hr-form-dialog
    ref="dialog"
    :title="i18n.t('settings.billing.chargeMaster.retire.title', { code: props.code })"
    size="sm"
    data-e2e="dialog-retire-service-code"
    @submit="onSubmit"
    @hide="emit('hide')"
  >
    <p>{{ i18n.t('settings.billing.chargeMaster.retire.textCaption') }}</p>

    <calendar-date-field
      v-model="lastBillableOn"
      :label="i18n.t('settings.billing.chargeMaster.retire.inputLastBillable')"
      :hint="i18n.t('settings.billing.chargeMaster.retire.hintLastBillable')"
      :min="props.validFrom"
      clearable
      data-e2e="input-retire-last-billable"
    />

    <!--
      SAID WHERE THE DECISION IS MADE, not afterwards in a toast. A retirement looks destructive and
      is not — the row stays, its charge stays, and everything already billed keeps resolving — so
      the reassurance belongs next to the control, where it can stop somebody hesitating over the
      right action.
    -->
    <hr-info-banner
      v-if="lastBillableOn !== null || isReopening"
      class="q-mt-md"
      :message="isReopening
        ? i18n.t('settings.billing.chargeMaster.retire.textReopens')
        : i18n.t('settings.billing.chargeMaster.retire.textKeepsHistory')"
    />

    <template #actions>
      <hr-dismiss-btn
        :label="i18n.t('common.buttonCancel')"
        @click="hide"
      />
      <hr-confirm-btn
        :label="isReopening
          ? i18n.t('settings.billing.chargeMaster.retire.buttonReopen')
          : i18n.t('settings.billing.chargeMaster.retire.buttonRetire')"
        :disable="!hasChange"
        type="submit"
        data-e2e="button-submit-retire-code"
      />
    </template>
  </hr-form-dialog>
</template>
